import { useEffect, useMemo } from 'react';
import { ALLOWED_SVG_ATTRS, ALLOWED_SVG_TAGS, ROOT_SVG_ATTRS } from '@lk/shared';
import { cn } from '../lib/cn';
import { reportFigureFailure, type FigureField } from '../lib/reportFigure';

/**
 * Малюнок до питання, який приходить від рушія у вигляді SVG-розмітки.
 *
 * Геометрія, розгортки, матриці й діаграми не мають сенсу словами: дитина
 * мусить БАЧИТИ фігуру. Тому генератори віддають готовий SVG у полі `passage`
 * (або в картинці варіанта), а не текстовий опис.
 *
 * Розмітка вставляється в документ, тому пропускається через СУВОРИЙ білий
 * список — див. `@lk/shared/svg-allowlist`. Дозволено рівно те, що вміє малювати
 * `packages/engine/src/generators/svg.ts`, і нічого понад це: ні `script`,
 * ні обробників подій, ні посилань, ні `style`. Джерело зараз довірене (наш
 * власний код на сервері), але банк питань поповнюється ще й LLM-пайплайном —
 * тож перевірка тут не зайва обережність, а єдине місце, де таку розмітку
 * взагалі можна зупинити.
 */

function sanitize(markup: string): string | null {
  if (typeof window === 'undefined') return null;
  const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;

  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== 'svg') return null;

  const walk = (node: Element): void => {
    for (const child of Array.from(node.children)) {
      if (!ALLOWED_SVG_TAGS.has(child.tagName.toLowerCase())) {
        child.remove();
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        if (!ALLOWED_SVG_ATTRS.has(attr.name.toLowerCase())) child.removeAttribute(attr.name);
      }
      walk(child);
    }
  };

  for (const attr of Array.from(root.attributes)) {
    const name = attr.name.toLowerCase();
    if (!ALLOWED_SVG_ATTRS.has(name) && !ROOT_SVG_ATTRS.has(name)) {
      root.removeAttribute(attr.name);
    }
  }
  walk(root);

  // Розмір веде ШИРИНА, а висота рахується з viewBox.
  // Раніше тут стояло height="100%": у контейнера без явної висоти це
  // невизначена величина, і фігура вилазила за рамку.
  root.removeAttribute('width');
  root.removeAttribute('height');
  root.setAttribute('width', '100%');
  if (!root.getAttribute('preserveAspectRatio')) {
    root.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  return new XMLSerializer().serializeToString(root);
}

/** Чи схоже значення на SVG-розмітку, а не на звичайний текст умови. */
export function looksLikeSvg(value: string | undefined): boolean {
  return Boolean(value && value.trimStart().startsWith('<svg'));
}

export interface SafeSvgProps {
  markup: string;
  /** Текстова альтернатива: без неї малюнок недоступний з екранним читачем. */
  label: string;
  className?: string;
  /**
   * Питання, якому належить малюнок, і місце малюнка в ньому.
   *
   * Потрібне рівно для одного: коли розмітка не пройшла санітайзер, ми
   * повертаємо `null` — і дитина бачить порожню рамку. Мовчки. Раніше про це
   * не дізнавався ніхто, і саме ця тиша дала попередній поломці прожити
   * стільки, скільки вона прожила. Тепер екран розповідає про свою невдачу,
   * а батько бачить список у кабінеті.
   *
   * Без `itemId` повідомлення не надсилається: без нього воно й не потрібне —
   * знати, що «десь щось не намалювалося», марно.
   */
  itemId?: string;
  field?: FigureField;
}

export function SafeSvg({
  markup,
  label,
  className,
  itemId,
  field = 'passage',
}: SafeSvgProps): React.JSX.Element | null {
  const clean = useMemo(() => sanitize(markup), [markup]);

  // Саме в ефекті, а не в тілі: рендер має лишатися чистим, а на сервері
  // (де `window` немає) `sanitize` повертає `null` завжди — і без цієї
  // умови ми б слали хибне повідомлення на кожен серверний рендер.
  useEffect(() => {
    if (!clean && typeof window !== 'undefined') reportFigureFailure(itemId, field);
  }, [clean, itemId, field]);

  if (!clean) return null;

  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        // `h-auto` обов'язковий: висоту диктує пропорція viewBox, а не контейнер.
        'block [&>svg]:block [&>svg]:h-auto [&>svg]:w-full [&>svg]:max-h-full',
        className,
      )}
      // Безпечно: розмітка щойно пройшла білий список вище — жодних
      // скриптів, обробників подій і посилань у ній лишитися не може.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
